/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Flutter SDK", description = "Hightouch's Flutter SDK for collecting events", lastUpdatedDate = new Date(1728259200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Flutter SDK",
        description: "Hightouch's Flutter SDK for collecting events",
        lastUpdatedDate: "Mon Oct 07 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "Initialization",
          "depth": 0,
          "id": "initialization"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }, {
          "value": "Reset",
          "depth": 1,
          "id": "reset"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      code: "code",
      pre: "pre",
      strong: "strong",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Flutter SDK makes it easy to track events from all types of Flutter applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Flutter SDK is hosted on ", _jsx(_components.a, {
          href: "https://pub.dev/packages/hightouch_events",
          children: "pub.dev"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You may install the SDK by adding it to your ", _jsx(_components.code, {
          children: "pubspec.yaml"
        }), " file:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-sh",
          children: "flutter pub add hightouch_events\n"
        })
      }), "\n", _jsx(_components.h2, {
        id: "initialization",
        children: "Initialization"
      }), "\n", _jsx(_components.p, {
        children: "You should initialize the SDK in the top-level app state and store it in a variable\nfor use across your application. For example, you may want to initialize it in\nyour app delegate."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["final ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "analytics"
          }), " = createClient(Configuration(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"WRITE_KEY\""
          }), "))", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Configuration options"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "writeKey"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "Your Hightouch write key."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "apiHost"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The API host to send the tracked events to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackApplicationLifecycleEvents"
              })
            }), _jsx(_components.td, {
              children: "Bool"
            }), _jsxs(_components.td, {
              children: ["Whether to automatically track application lifecycle events (like opening the application). Defaults to ", _jsx(_components.code, {
                children: "false"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.identify"
        }), " method sends an ", _jsx(_components.a, {
          href: "../event-spec#identify-events",
          children: _jsx(_components.code, {
            children: "identify"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-less",
          children: [_jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "analytics"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".identify"
          }), "(", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user-123\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "userTraits"
          }), ": UserTraits(", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "email"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user@example.com\""
          }), "));\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.track"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#track-events",
          children: _jsx(_components.code, {
            children: "track"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "track"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"View Product\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": {", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"productId\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), "});\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.screen"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#screen-events",
          children: _jsx(_components.code, {
            children: "screen"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "screen"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Home\""
          }), ");\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "analytics.group"
        }), " method sends a ", _jsx(_components.a, {
          href: "../event-spec#group-events",
          children: _jsx(_components.code, {
            children: "group"
          })
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-less",
          children: [_jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "analytics"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".group"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"group-123\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "groupTraits"
          }), ": GroupTraits(", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Group 123\""
          }), "));\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The Flutter SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush()"
        }), "\nmethod."]
      }), "\n", _jsx(_components.h3, {
        id: "reset",
        children: "Reset"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method resets the ", _jsx(_components.code, {
          children: "identify"
        }), " and ", _jsx(_components.code, {
          children: "group"
        }), " for the local session.\nSpecifically, it resets the ", _jsx(_components.code, {
          children: "anonymousId"
        }), ", ", _jsx(_components.code, {
          children: "userId"
        }), ", ", _jsx(_components.code, {
          children: "referrer"
        }), ", and ", _jsx(_components.code, {
          children: "traits"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method should be called when users log out. This way, if the user\nlogs back in with another account, the userIds and traits from the different\nsessions remain separate."]
      })]
    });
  }
}
export default MDXContent;
